import React, { useState } from "react";
import { Search, Plus, X } from "lucide-react";
import { useContentManagementContext } from "../ContentManagementContext";
import CreateBlogModal from "./CreateBlogModal";
import CreateFAQModal from "./CreateFAQModal";

const ContentLibraryHeader = ({ activeTab, searchTerm, onSearchChange }) => {
  const { 
    blogFilters, 
    faqFilters, 
    updateBlogFilters, 
    updateFaqFilters, 
    resetBlogFilters, 
    resetFaqFilters 
  } = useContentManagementContext();
  const currentFilters = activeTab === 'faqs' ? faqFilters : blogFilters;
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState(currentFilters.status || 'all');
  const [sortBy, setSortBy] = useState(currentFilters.sortBy || (activeTab === 'faqs' ? 'priority' : 'createdAt'));
  const [sortOrder, setSortOrder] = useState(currentFilters.sortOrder || 'desc');
  const [categoryFilter, setCategoryFilter] = useState(currentFilters.category || 'all');
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Handle filter changes
  const handleFilterChange = () => {
    if (activeTab === "blog-posts") {
      updateBlogFilters({
        status: statusFilter,
        sortBy: sortBy,
        sortOrder: sortOrder
      });
    } else if (activeTab === "faqs") {
      updateFaqFilters({
        status: statusFilter,
        category: categoryFilter,
        sortBy: sortBy,
        sortOrder: sortOrder
      });
    }
  };

  // Reset all filters
  const handleResetFilters = () => {
    onSearchChange('');
    setStatusFilter('all');
    setSortBy(activeTab === 'faqs' ? 'priority' : 'createdAt');
    setSortOrder('desc');
    setCategoryFilter('all');
    
    if (activeTab === 'faqs') {
      resetFaqFilters();
    } else {
      resetBlogFilters();
    }
  };

  // Check if any filters are active
  const hasActiveFilters = searchTerm || statusFilter !== 'all' || 
    sortBy !== (activeTab === 'faqs' ? 'priority' : 'createdAt') || 
    sortOrder !== 'desc' || 
    (activeTab === 'faqs' && categoryFilter !== 'all');
  const getHeaderTitle = () => {
    switch (activeTab) {
      case "blog-posts":
        return "Content Library";
      case "announcements":
        return "Platform Announcements";
      case "roadmaps":
        return "Roadmaps";
      case "faqs":
        return "FAQs";
      default:
        return "Content Library";
    }
  };

  const getCreateButtonText = () => {
    switch (activeTab) {
      case "blog-posts":
        return "Create blog post";
      case "announcements":
        return "Create announcement";
      case "roadmaps":
        return "Create roadmap";
      case "faqs":
        return "Create FAQ";
      default:
        return "Create content";
    }
  };

  const getSearchPlaceholder = () => {
    switch (activeTab) {
      case "blog-posts":
        return "Search blog posts...";
      case "announcements":
        return "Search announcements...";
      case "roadmaps":
        return "Search roadmaps...";
      case "faqs":
        return "Search FAQs...";
      default:
        return "Search...";
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
      <div className="flex flex-col gap-4">
        {/* Header Row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Content Library Title */}
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{getHeaderTitle()}</h2>
          
          {/* Search and Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            {/* Search Bar */}
            <div className="relative flex-1 sm:max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={getSearchPlaceholder()}
                value={searchTerm || ''}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              />
              {searchTerm && (
                <button
                  onClick={() => onSearchChange('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {/* Create Button */}
            <button 
              onClick={() => setShowCreateModal(true)}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-secondary text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>{getCreateButtonText()}</span>
            </button>
          </div>
        </div>

        {/* FAQ Filters Panel */}
        {showFilters && activeTab === "faqs" && (
          <div className="border-t border-gray-200 pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                >
                  <option value="all">All Categories</option>
                  <option value="Getting Started">Getting Started</option>
                  <option value="API">API</option>
                  <option value="Billing">Billing</option>
                  <option value="General">General</option>
                </select>
              </div>

              {/* Sort By */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                >
                  <option value="priority">Priority</option>
                  <option value="createdAt">Created Date</option>
                  <option value="views">Views</option>
                  <option value="helpful">Helpful</option>
                </select>
              </div>

              {/* Sort Order */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Order</label>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={handleResetFilters}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Reset Filters
              </button>
              <button
                onClick={handleFilterChange}
                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors"
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Blog Modal */}
      {activeTab === "blog-posts" && (
        <CreateBlogModal 
          isOpen={showCreateModal} 
          onClose={() => setShowCreateModal(false)} 
        />
      )}

      {/* Create FAQ Modal */}
      {activeTab === "faqs" && (
        <CreateFAQModal 
          isOpen={showCreateModal} 
          onClose={() => setShowCreateModal(false)} 
        />
      )}
    </div>
  );
};

export default ContentLibraryHeader;
